import { truncate } from 'lodash'
import { DATA_BINDING as APP_DEF_ID } from '@wix/app-definition-ids'
import {
  ROUTER_DATASET,
  READ,
  type ReadWriteMode,
} from '@wix/wix-data-client-types'
import type { PageType } from '../../../business-data/dynamicPages'
import { PAGE_TYPES } from '../../../business-data/dynamicPages'
import { parseUrlPattern } from '../routing-url/utils'
import { isClassicEditor, type EDITOR_TYPE } from '../../../infra/editorType'

const capitalize = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1)

export const generatePageTitle = (
  collectionName: string,
  pattern: string,
): string => {
  const { fields } = parseUrlPattern(pattern)
  return fields.length > 0
    ? `${fields.map(field => `{${field}}`).join(', ')}`
    : collectionName
}

// TODO: Replace this when platform exposes sanitization (https://jira.wixpress.com/browse/EP-2069)
export const sanitizeCollectionName = (collectionName: string): string => {
  const chevrons = /<|>/g
  const emojis =
    /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32-\ude3a]|[\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g // eslint-disable-line
  return collectionName.replace(chevrons, '').replace(emojis, '').trim()
}

const MAX_PAGE_NAME_LENGTH = 39

export const truncatePageTitle = (pageName: string): string =>
  truncate(pageName, { length: MAX_PAGE_NAME_LENGTH })

const getLengthsForTruncatedParts = ({
  name,
  suffix,
}: {
  name: string
  suffix: string
}): { nameLength: number; suffixLength: number } => {
  const totalAvailableLength = MAX_PAGE_NAME_LENGTH - ' ()'.length
  const shorterPartsLength = Math.min(
    name.length,
    suffix.length,
    Math.floor(totalAvailableLength / 2),
  )
  const longerPartsLength = totalAvailableLength - shorterPartsLength
  return name.length < suffix.length
    ? { nameLength: shorterPartsLength, suffixLength: longerPartsLength }
    : { nameLength: longerPartsLength, suffixLength: shorterPartsLength }
}

export const truncatePageNameParts = ({
  name,
  suffix,
}: {
  name: string
  suffix: string
}): { truncatedName: string; truncatedSuffix: string } => {
  const { nameLength, suffixLength } = getLengthsForTruncatedParts({
    name,
    suffix,
  })
  return {
    truncatedName: truncate(name, { length: nameLength }),
    truncatedSuffix: truncate(suffix, { length: suffixLength }),
  }
}

const getPageSuffix = ({
  isEditPage,
  isCategoryPage,
  hasFields,
}: {
  isEditPage?: boolean
  isCategoryPage?: boolean
  hasFields: boolean
}): string => {
  if (isEditPage) {
    return 'Manage Item'
  }
  if (isCategoryPage) {
    return 'Category'
  }
  return hasFields ? 'Item' : 'List'
}

export const getPageName = ({
  collectionName,
  pattern,
  isEditPage, // https://wix.slack.com/archives/C05URCFF5BQ/p1713348004513049
  isCategoryPage,
}: {
  collectionName: string
  pattern: string
  isEditPage?: boolean
  isCategoryPage?: boolean
}): string => {
  const { fields: fieldNames, nonFields } = parseUrlPattern(pattern)
  const name = [sanitizeCollectionName(collectionName)]
    .concat(isEditPage ? [] : nonFields)
    .map(capitalize)
    .join(' ')

  const suffix = getPageSuffix({
    isEditPage,
    isCategoryPage,
    hasFields: fieldNames.length > 0,
  })

  const { truncatedName, truncatedSuffix } = truncatePageNameParts({
    name,
    suffix,
  })
  return `${truncatedName} (${truncatedSuffix})`
}

export const getRouterDatasetName = (
  collectionName: string,
  pageType: PageType = PAGE_TYPES.ITEM,
): string =>
  pageType === PAGE_TYPES.ITEM
    ? capitalize(collectionName) + ' Item'
    : capitalize(collectionName)

export const getRouterDatasetDefinition = ({
  collectionName,
  pageType,
  editorType,
  displayName,
  readWriteType = READ,
}: {
  collectionName: string
  pageType: PageType
  editorType: EDITOR_TYPE
  displayName?: string
  readWriteType?: ReadWriteMode
}) => ({
  componentType: 'platform.components.AppController',
  connections: {
    type: 'ConnectionList',
    items: [
      {
        type: 'WixCodeConnectionItem',
        role: 'dynamicDataset',
      },
    ],
  },
  data: {
    controllerType: ROUTER_DATASET,
    name: displayName ?? getRouterDatasetName(collectionName, pageType),
    applicationId: APP_DEF_ID,
    settings: JSON.stringify({
      dataset: {
        readWriteType,
      },
    }),
  },
  layout: isClassicEditor(editorType)
    ? {
        x: 0,
        y: 0,
        fixedPosition: true,
      }
    : {
        x: 450,
        y: 70,
      },
})
